import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { computed, action, isArrayLike } from 'mobx';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '../utils/propStyles';
import Icon from './Icon';
import DownloadControls from './Document/DownloadControls';
import Translate from './Translate';
import WatchListToggle from './WatchListToggle';
import ChangeIndicator from './ChangeIndicator';
import DocumentMenu from './Document/DocumentMenu';
import Tooltip from './Tooltip';
import { ShareclassCardList, ShareclassCardRow } from './ShareclassCards';
import ColumnDropdown from './ColumnDropdown';
import StarRating from './StarRating';
import DynamicLinkProps from '../models/components/DynamicLinkProps';
import DynamicLink from './DynamicLink';
import InlineDisclaimers from './InlineDisclaimers';
import {
    DataStore,
    EntityStore,
    HistoryStore,
    SuperscriptScope,
} from '../stores';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { isGUID } from '../utils/isGUID';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../models/configuration/explorer/DownloadControls';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { Group } from '../models/configuration/explorer/Group';

const ShareclassCardLabel = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardLabel.defaultProps = {
    fontFamily: 'heading',
    fontWeight: 'medium',
};

const ShareclassCardValue = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardValue.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'body',
    fontWeight: 'regular',
};

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

const SeparatorWrapper = styled<any, any>('span')`
    ${propStyles};
    white-space: pre;
`;

interface ShareclassCardLabelValueProps {
    theme?: any;
    /** Injected */
    dataStore?: DataStore;
    historyStore?: HistoryStore;
    entityStore?: EntityStore;
    /**  */
    column: ColumnConfig;
    columnIndex: number;
}

interface WorkingColumnConfig extends ColumnConfig {
    multiple?: boolean;
    url?: string;
    cellDisclaimers?: Array<any>;
    values?: Array<any>;
}

interface WorkingGroupConfig extends Group {
    multiple?: boolean;
    url?: string;
    cellDisclaimers?: Array<any>;
    values?: Array<any>;
}

@inject('dataStore', 'historyStore', 'entityStore')
@observer
class ShareclassCardLabelValue extends Component<
    ShareclassCardLabelValueProps
> {
    // This is to store the column value without any html elements (replacementValue) for use of the data layer logging
    rawValue: string = '';
    constructor(props) {
        super(props);

        if (this.props.column.groups) {
            this.setInitialGroupIndex();
        }
    }

    @computed
    get columnStore() {
        return this.props.dataStore.columnStore;
    }

    @action
    setInitialGroupIndex = () => {
        const { column, columnIndex } = this.props;
        let selectedGroupIndex;
        if (this.columnStore.hasColumnGroupsSet) {
            const columnGroups = this.columnStore.columnGroups;
            const configColumnKey = column.key;
            const columnGroupSelectedValue = !isGUID(configColumnKey)
                ? columnGroups[configColumnKey]
                : columnGroups[columnIndex];

            if (isNaN(Number(columnGroupSelectedValue))) {
                selectedGroupIndex = column.groups.findIndex(
                    group => group.key === columnGroupSelectedValue
                );
            } else {
                selectedGroupIndex = Number(columnGroupSelectedValue);
            }
        }

        if (
            isNullOrUndefined(selectedGroupIndex) ||
            selectedGroupIndex === -1
        ) {
            const selectedConfigGroupIndex = column.groups.findIndex(
                c => c.selected
            );

            selectedGroupIndex = this.selectedGroupIndex
                ? this.selectedGroupIndex
                : selectedConfigGroupIndex > -1
                ? selectedConfigGroupIndex
                : 0;
        }

        this.columnStore.selectedGroupMap.set(
            this.props.columnIndex,
            selectedGroupIndex
        );
    };

    @computed
    get selectedGroupIndex() {
        let index = this.columnStore.selectedGroupMap.get(
            this.props.columnIndex
        );
        if (this.props.column.groups) {
            if (index >= this.props.column.groups.length) {
                const selectedConfigGroupIndex = this.props.column.groups.findIndex(
                    c => c.selected
                );
                // tslint:disable-next-line: prefer-conditional-expression
                if (selectedConfigGroupIndex > -1) {
                    index = selectedConfigGroupIndex;
                } else {
                    index = 0;
                }
            }
        }
        return index;
    }

    @computed
    get column() {
        return this.props.column.groups
            ? (this.props.column.groups[
                  this.selectedGroupIndex
              ] as WorkingGroupConfig)
            : (this.props.column as WorkingColumnConfig);
    }

    @computed
    get downloadControlActions(): DownloadControlActions {
        const { dataStore } = this.props;
        return dataStore?.config.getDownloadControlActionsOrDefault(
            this.props.column?.actions
        );
    }

    getGroupOrDropdownContent = (
        column,
        dynamicLinkProps: DynamicLinkProps
    ) => {
        const { theme } = this.props;
        const { selectedGroupIndex } = this;
        return (
            <Fragment>
                {column.type === 'group' ? (
                    <ShareclassCardLabel {...theme.ShareclassCardGroupLabel}>
                        <Translate>{column.label}</Translate>
                        <Tooltip
                            content={column.disclaimers}
                            accessibilityContext={`for disclaimers of the ${column.label} column`}
                            analyticsContext={{
                                metricContext: `shareclassCard-${camelCase(
                                    column.label
                                )}`,
                                source: 'ShareclassCardLabel',
                                tooltipCode: column.disclaimerTypes,
                            }}
                        />
                    </ShareclassCardLabel>
                ) : (
                    <ColumnDropdown
                        column={column}
                        selectedGroupIndex={selectedGroupIndex}
                        theme={{
                            ...theme,
                            ColumnDropdownWrapper: {
                                ...theme.ColumnDropdownWrapper,
                                ...theme.ShareclassCardRowDropdownWrapper,
                            },
                            ColumnDropdownInnerWrapper: {
                                ...theme.ColumnDropdownInnerWrapper,
                                ...theme.ShareclassCardRowDropdownInnerWrapper,
                            },
                            ColumnDropdownGroupLabel: {
                                ...theme.ColumnDropdownGroupLabel,
                                ...theme.ShareclassCardRowDropdownGroupLabel,
                            },
                            ColumnDropdownChevron: {
                                ...theme.ColumnDropdownChevron,
                                ...theme.ShareclassCardRowDropdownChevron,
                            },
                            ColumnDropdownMenu: {
                                ...theme.ColumnDropdownMenu,
                                ...theme.ShareclassCardRowDropdownMenu,
                            },
                            ColumnDropdownList: {
                                ...theme.ColumnDropdownList,
                                ...theme.ShareclassCardRowDropdownList,
                            },
                            ColumnDropdownListItem: {
                                ...theme.ColumnDropdownListItem,
                                ...theme.ShareclassCardRowDropdownListItem,
                            },
                        }}
                    />
                )}
                <ShareclassCardList flex="1 0 100%" paddingX="0">
                    {column.values &&
                        column.values.map((c, cIndex) => (
                            <ShareclassCardRow
                                key={`${c.label}-${cIndex}`}
                                paddingY="sm"
                            >
                                <Fragment>
                                    <ShareclassCardLabel
                                        paddingX="md"
                                        {...theme.ShareclassCardLabel}
                                        {...theme.ShareclassCardLabelNested}
                                    >
                                        <Translate>{c.label}</Translate>
                                        <Tooltip
                                            content={c.disclaimers}
                                            analyticsContext={{
                                                metricContext: `shareclassCard-${camelCase(
                                                    c.label
                                                )}`,
                                                source: 'ShareclassCardLabel',
                                                tooltipCode: c.disclaimerTypes,
                                            }}
                                        />
                                    </ShareclassCardLabel>
                                    {this.getShareclassCardValue(
                                        c,
                                        dynamicLinkProps,
                                        { ...theme.ShareclassCardValueNested }
                                    )}
                                </Fragment>
                            </ShareclassCardRow>
                        ))}
                </ShareclassCardList>
            </Fragment>
        );
    };
    getDownloadContent = (column: WorkingColumnConfig) => {
        const { theme, dataStore, entityStore, children } = this.props;
        const {
            value: shareClass,
            shareClassDownloads,
            expanded,
        } = entityStore;
        const content = (
            <Fragment>
                <DownloadControls
                    count={entityStore.downloadCount}
                    componentThemeKey="ShareclassCard"
                    hidePreview={dataStore.isZipDocument(shareClassDownloads)}
                    onClick={e => {
                        e.stopPropagation();
                        dataStore.downloadDocuments(
                            shareClassDownloads,
                            shareClass
                        );
                    }}
                    href={dataStore.getDocumentLink(
                        shareClassDownloads,
                        shareClass
                    )}
                    onPreviewClick={(
                        e,
                        elementOptions: PreviewActionOptions
                    ) => {
                        e.stopPropagation();
                        dataStore.previewDocuments(
                            shareClassDownloads,
                            shareClass,
                            elementOptions
                        );
                    }}
                    showLabel={false}
                    label={dataStore.getFileName(shareClassDownloads)}
                    fileName={dataStore.getFileName(
                        shareClassDownloads,
                        shareClass
                    )}
                    metricContext={`shareclassCard-${camelCase(
                        column.label
                    )}-${camelCase(shareClass.clientCode)}`}
                    superscriptScope={SuperscriptScope.module}
                    document={shareClassDownloads}
                    documentDisplayName={camelCase(shareClass.clientCode)}
                    actions={this.downloadControlActions}
                    disableExtendedInfo={column.disableExtendedInfo}
                />
                <Icon
                    fileName={
                        expanded
                            ? 'DocumentDropdownIconExpanded'
                            : 'DocumentDropdownIcon'
                    }
                    height="1.4em"
                    width="1.4em"
                    {...theme.ShareclassCardDownloadIcon}
                />
            </Fragment>
        );
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                <ShareclassCardValue>
                    <InlineDisclaimers
                        conditionalDisclaimers={column.cellDisclaimers}
                        superscriptScope={SuperscriptScope.module}
                        analyticsContext={{
                            source: 'shareclassCardValue',
                            sourceContext: content,
                        }}
                    >
                        {content}
                    </InlineDisclaimers>
                </ShareclassCardValue>
                {expanded && children}
            </Fragment>
        );
    };
    getWatchlistContent = column => {
        const { entityStore } = this.props;
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                <WatchListToggle
                    onClick={e => {
                        e.stopPropagation();
                        entityStore.toggleWatchList();
                    }}
                    active={entityStore.inWatchList}
                    fontSize="1em"
                    accessibilityContext={`for share class identified by ${entityStore.value.clientCode}`}
                    metricContext={camelCase(entityStore.value.clientCode)}
                />
            </Fragment>
        );
    };
    getDocumentContent = column => {
        const { theme, dataStore, entityStore } = this.props;
        const shareClassLabel = this.getShareclassCardLabel(column);

        return (
            <Fragment>
                {Array.isArray(column.value) && column.value.length > 0 ? (
                    <Fragment>
                        <InlineDisclaimers
                            conditionalDisclaimers={column.cellDisclaimers}
                            superscriptScope={SuperscriptScope.module}
                            analyticsContext={{
                                source: 'shareclassCardLabel',
                                sourceContext: shareClassLabel,
                            }}
                        >
                            {shareClassLabel}
                        </InlineDisclaimers>
                        <DocumentMenu
                            superscriptScope={SuperscriptScope.module}
                            documents={column.value}
                            showSingleDocument={column.showSingleDocument}
                            showSingleDocumentLabel={
                                column.showSingleDocumentLabel
                            }
                            showCount={true}
                            displayOption={'inline'}
                            theme={{
                                ...theme,
                                ...{
                                    DocumentDropdownButton: {
                                        justifyContent: 'flex-end',
                                    },
                                    DownloadButtonIconNoLabel: {
                                        margin: '0 0 0 auto',
                                    },
                                },
                            }}
                            metricContext={`shareclassCard-${camelCase(
                                column.label
                            )}-${camelCase(entityStore.value.clientCode)}`}
                            disableExtendedInfo={column.disableExtendedInfo}
                        />
                    </Fragment>
                ) : (
                    <Fragment>
                        {shareClassLabel}
                        <ShareclassCardValue {...theme.ShareclassCardValue}>
                            <Translate>
                                {column.noDataPlaceholder ||
                                    dataStore.config.noDataPlaceholder}
                            </Translate>
                        </ShareclassCardValue>
                    </Fragment>
                )}
            </Fragment>
        );
    };
    getPricesContent = column => {
        const { historyStore, entityStore } = this.props;
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                {column.data.performanceCheck && !entityStore.hasPerformance ? (
                    <Translate>{column.data.value}</Translate>
                ) : (
                    <DownloadControls
                        display="inline-flex"
                        onClick={() => {
                            historyStore.getPriceHistory(column, null);
                        }}
                        hidePreview={true}
                        count={1}
                        showCount={false}
                        // Due to historic prices building up filename on the response, we can not accurately set this. Default to historic_prices.csv
                        fileName={'historic_prices.csv'}
                        metricContext={`shareclassCard-${camelCase(
                            column.label
                        )}-${camelCase(entityStore.value.clientCode)}`}
                        superscriptScope={SuperscriptScope.module}
                        disableExtendedInfo={column.disableExtendedInfo}
                        actions={this.downloadControlActions}
                    />
                )}
            </Fragment>
        );
    };
    getStarsContent = column => {
        const { theme, entityStore } = this.props;
        if (column.data.performanceCheck && !entityStore.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        }
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                <InlineDisclaimers
                    conditionalDisclaimers={column.cellDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        source: 'shareclassCardLabel',
                        sourceContext: column.data.value,
                    }}
                >
                    <StarRating
                        rating={column.data.value}
                        noDataPlaceholder={column.noDataPlaceholder}
                        showEmptyStars={column.type === 'starsOutOf'}
                        display="block"
                        theme={{
                            ...theme,
                            ...theme.StarRatingColumnWrapper,
                            StarIcon: {
                                ...starIconStyleProps,
                                ...theme.StarIconColumn,
                            },
                            StarOutlineIcon: {
                                ...starIconStyleProps,
                                ...theme.StarOutlineIconColumn,
                            },
                        }}
                    />
                </InlineDisclaimers>
            </Fragment>
        );
    };
    getSimpleContent = (column, dynamicLinkProps: DynamicLinkProps) => {
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                {this.getShareclassCardValue(column, dynamicLinkProps)}
            </Fragment>
        );
    };
    getShareclassCardLabel = (
        column,
        labelProps: any = {},
        themeKey = 'ShareclassCardLabel'
    ) => {
        const { theme } = this.props;
        const conditionalDisclaimers =
            column.disclaimerTypes &&
            column.disclaimerTypes.map(type => {
                return {
                    disclaimerType: type,
                };
            });
        return (
            <ShareclassCardLabel {...labelProps} {...theme[themeKey]}>
                <InlineDisclaimers
                    conditionalDisclaimers={conditionalDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        source: 'shareclassCardLabel',
                        sourceContext: column.label,
                    }}
                >
                    <Translate>{column.label}</Translate>
                </InlineDisclaimers>
            </ShareclassCardLabel>
        );
    };
    getShareclassCardValue = (
        column,
        dynamicLinkProps: DynamicLinkProps,
        shareclassCardValueProps: any = {},
        themeKey: string = 'ShareclassCardValue'
    ) => {
        const { theme, dataStore } = this.props;
        const dataRecords = isArrayLike(column.data)
            ? column.data
            : [column.data];

        const values = [];
        let replacementValue = '';
        dataRecords.forEach((dataRecord, index) => {
            replacementValue += `${dataRecord.value}${
                column.multiple && dataRecord.separator
                    ? dataRecord.separator
                    : ''
            }`;
            let inner = (
                <Fragment>
                    <Translate>{dataRecord.value}</Translate>
                    {column.multiple && dataRecord.separator && (
                        <SeparatorWrapper>
                            {dataRecord.separator}
                        </SeparatorWrapper>
                    )}
                </Fragment>
            );
            if (dataRecord.showChange) {
                inner = (
                    <ChangeIndicator
                        dataStore={dataStore}
                        showChange={dataRecord.showChange}
                        value={dataRecord.value}
                        separator={dataRecord.separator}
                    />
                );
            }
            values.push(
                <Fragment key={`${dataRecord.code}-${index}`}>{inner}</Fragment>
            );
        });
        this.rawValue = replacementValue;
        if (dynamicLinkProps.showLink) {
            dynamicLinkProps.ariaLabelReplacements = {
                label: column.label,
                value: replacementValue,
            };
            dynamicLinkProps.ariaLabelKey = 'shareClassRowLinks';
        }
        return (
            <ShareclassCardValue
                {...theme[themeKey]}
                {...shareclassCardValueProps}
            >
                <InlineDisclaimers
                    conditionalDisclaimers={column.cellDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        source: 'shareclassCardLabel',
                        sourceContext: values,
                    }}
                >
                    <DynamicLink {...dynamicLinkProps}>{values}</DynamicLink>
                </InlineDisclaimers>
            </ShareclassCardValue>
        );
    };
    onClick = (link: string) => {
        const { dataStore } = this.props;
        dataStore.logDataLayer(
            'entity_data_card',
            {
                [this.column.label]: this.rawValue,
                url: link,
            },
            'click'
        );
    };
    getContent = column => {
        const { theme, dataStore, entityStore } = this.props;
        const dynamicLinkProps: DynamicLinkProps = {
            dataStore,
            queryContext: entityStore.queryContext,
            config: column,
            showLink: false,
            theme: theme.ShareclassColumnLink,
            metricContext: `shareclassCard-${camelCase(
                column.label
            )}-${camelCase(entityStore.value.clientCode)}`,
            onClick: this.onClick,
        };
        let content;
        if (['group', 'dropdown'].includes(column.type)) {
            dynamicLinkProps.showLink = true;
            content = this.getGroupOrDropdownContent(column, dynamicLinkProps);
        } else if (column.type === 'download') {
            content = this.getDownloadContent(column);
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            content = this.getWatchlistContent(column);
        } else if (column.type === 'document') {
            content = this.getDocumentContent(column);
        } else if (column.type === 'prices') {
            content = this.getPricesContent(column);
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            content = this.getStarsContent(column);
        } else {
            dynamicLinkProps.showLink = true;
            content = this.getSimpleContent(column, dynamicLinkProps);
        }

        return content;
    };

    render() {
        return this.getContent(this.column);
    }
}

export default withTheme(ShareclassCardLabelValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassCardLabelValue.tsx